import React, { useEffect, useRef, useState } from "react";
import { useFormattedMessage, useUtils } from "hooks";
import { brokerInvestmentProductPage } from "i18n/messages/meta";
import BrokerContainer from "components/BrokerContainer";
import CustomFormatNumber from "components/CustomFormatNumber";
import CustomDataTable from "components/CustomDataTable";
import { getTranslatedPrimaryMarketProjectStatusDropdownList } from "constants/index";
import { getProjectInvestmentStatusLookUp } from "utils/statusLookUp";
import Avatar from "components/CustomDataTable/Avatar";
import ColorIndicator from "components/ColorIndicator";
import ProductDetailModal from "components/Broker/ProductDetailsSidepage";
import { addOverlay, removeOverlay, sideModalScrollHidden } from "utils";
import { fetchProductCatalog, fetchProductCatalogProjects, getProductProvisionByIds } from "api";
import { PROJECT_STATUS } from "constants/status";

const messages = {
    investmentProduct: "investment_products",
    yield: "yield",
    commission: "commission",
    status: "status",
    product: "product",
};

const ProductsPage = () => {
    const isFirstRender = useRef(null);
    const currentTenant = useUtils()?.tenant?.TechnicalName;
    const { formatMessage } = useFormattedMessage();
    const PROJECT_STATUS_LIST = getTranslatedPrimaryMarketProjectStatusDropdownList();
    const [selectedProductStatus, setSelectedProductStatus] = useState([]);
    const [products, setProducts] = useState([]);
    const [filteredProductData, setFilteredProductData] = useState([]);
    const [search, setSearch] = useState("");
    const [isDetailModalOpen, setDetailModalOpen] = useState(false);
    const [singleProductData, setSingleProductData] = useState(null);
    const [isProductListLoading, setProductListLoading] = useState(true);

    const productColumn = [
        {
            name: formatMessage(messages.product),
            selector: (row) => row?.title,
            sortable: true,
            cell: (row) => {
                const updatedType = row?.TypeProduct?.split("-")?.[2]
                    ? `${row?.TypeProduct?.split("-")?.[0]} - ${row?.TypeProduct?.split("-")?.[2]}`
                    : row?.TypeProduct;
                return (
                    <div className="d-flex align-center pointer-none">
                        <Avatar className="mr-6" path={row?.images?.teaserImage?.path} circle />
                        <div>
                            <p className="text-14 mb-1 product-title-overflow">{row?.title}</p>
                            <div className="d-flex">
                                <span className="text-12 another-grey-color product-title-overflow">
                                    {row?.CategoryProduct ? `${row?.CategoryProduct} - ${updatedType}` : updatedType}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            },
            width: "400px",
        },
        {
            name: formatMessage(messages.yield),
            selector: (row) => row?.financials?.totalReturnEstimate,
            sortable: true,
            cell: (row) => (
                <CustomFormatNumber className="pointer-none" value={row?.financials?.totalReturnEstimate} fixed={1} symbol="%" />
            ),
            width: "120px",
        },
        {
            name: formatMessage(messages.commission),
            selector: (row) => row?.provision,
            sortable: true,
            cell: (row) => <CustomFormatNumber className="pointer-none" value={row?.provision} fixed={2} symbol="%" />,
            width: "140px",
        },
        {
            name: formatMessage(messages.status),
            selector: (row) => row?.status?.status,
            sortable: true,
            cell: (row) => {
                const { indicatorColor, text } = getProjectInvestmentStatusLookUp(row?.status?.status);
                return (
                    <div className={`pointer-none indicator-container pointer-none d-flex w-100`}>
                        <ColorIndicator color={indicatorColor} />
                        <div className="remain-content">
                            <p className="text-14 mb-1">{text}</p>
                        </div>
                    </div>
                );
            },
            minWidth: "210px",
        },
    ];

    const productRowClicked = (data) => {
        addOverlay();
        sideModalScrollHidden(true);
        setDetailModalOpen(true);
        setSingleProductData(data);
    };

    const onCloseButtonClick = () => {
        setDetailModalOpen(false);
        removeOverlay();
        sideModalScrollHidden(false);
        setSingleProductData(null);
    };

    function getUniqueProducts(productList) {
        const uniqueProductIds = new Set(productList.map((item) => item.projectID));
        return Array.from(uniqueProductIds);
    }

    function mergeProvisionData(products, provisions) {
        const provisionMap = provisions.reduce((map, provision) => {
            map[provision.productID] = provision.provisions?.partnerProvision;
            return map;
        }, {});

        return products.map((product) => {
            return {
                ...product,
                provision: provisionMap[product.projectID] || 0,
            };
        });
    }

    function mergeProductData(projects, products) {
        const productMap = products.reduce((map, product) => {
            map[product.ProductID] = {
                ...product,
            };
            return map;
        }, {});

        return projects.map((project) => {
            return {
                ...project,
                TypeProduct: productMap?.[project.projectID]?.TypeProduct,
                CategoryProduct: productMap?.[project.projectID]?.CategoryProduct,
            };
        });
    }

    const fetchInvestmentProduct = () => {
        setProductListLoading(true);
        // const response =
        let primaryMarketProducts = [];
        fetchProductCatalogProjects()
            .then((res) => {
                const productCatalog = res?.data?.data?.productCatalogProjects?.projects;
                const primaryMarketProductStatus = [
                    PROJECT_STATUS.COMING_SOON,
                    PROJECT_STATUS.ADVERTISING,
                    PROJECT_STATUS.FUNDING_ACTIVE,
                ];
                primaryMarketProducts = productCatalog?.filter((item) =>
                    primaryMarketProductStatus.includes(item?.status?.status),
                );

                return getProductProvisionByIds({
                    productIDs: getUniqueProducts(primaryMarketProducts),
                    marketplace: currentTenant,
                });
            })
            .then((res) => {
                const { provisions } = res?.data;
                const productWithProvision = mergeProvisionData(primaryMarketProducts, provisions || []);
                return fetchProductCatalog(`
                    ProductID
                    CategoryProduct
                    TypeProduct
                    `).then((res) => {
                    const products = res?.data?.data?.productCatalog?.productCatalog?.LinkProducts;
                    const updatedProduct = mergeProductData(productWithProvision, products || []);

                    setProducts([...updatedProduct]);
                    setFilteredProductData([...updatedProduct]);
                });
            })
            .catch((err) => {
                console.error("Error occurred:", err);
            })
            .finally(() => {
                setProductListLoading(false);
            });
    };

    useEffect(() => {
        !isFirstRender?.current && fetchInvestmentProduct();
        isFirstRender.current = true;
    }, []);

    return (
        <BrokerContainer headTitle={formatMessage(brokerInvestmentProductPage.title)} menu="Partner" className="broker-page">
            <ProductDetailModal open={isDetailModalOpen} onCloseButtonClick={onCloseButtonClick} data={singleProductData} />

            <h4 className="mt-0">{formatMessage(messages.investmentProduct)}</h4>
            <CustomDataTable
                columns={productColumn}
                data={products || []}
                filteredData={filteredProductData || []}
                onRowClicked={productRowClicked}
                paginationServer={false}
                progressPending={isProductListLoading}
                onSearchAndFilter={(data) => setFilteredProductData(data)}
                searchBoxFields={{
                    isShow: true,
                    onChange: setSearch,
                    value: search,
                    searchBy: ["title", "financials.totalReturnEstimate", "projectID", "status.status"],
                }}
                selectFilterFields={[
                    {
                        label: formatMessage(messages.status),
                        value: selectedProductStatus,
                        onChange: setSelectedProductStatus,
                        options: PROJECT_STATUS_LIST,
                        isMulti: true,
                        filterBy: "status.status",
                    },
                ]}
            />
        </BrokerContainer>
    );
};

export default ProductsPage;
